import { Link, useParams } from "react-router-dom";
import { ArrowRight, MapPin, Phone, Mail, ShieldCheck } from "lucide-react";
import Seo from "../components/Seo";
import FaqAccordion from "../components/FaqAccordion";
import CtaBand from "../components/CtaBand";
import ArticleCard from "../components/ArticleCard";
import CityMap from "../components/CityMap";
import NotFound from "./NotFound";
import { Reveal, Eyebrow, ChapterHeading } from "../components/Reveal";
import { SITE, SERVICES, getLocation, IMAGES, hash } from "../data/site";
import { cityContent } from "../data/content";
import { citySkyline } from "../data/cityImages";
import { cityGuide } from "../data/cityGuides";
import keepPaths from "../data/keepPaths.json";

const KEEP = new Set(keepPaths);
import { articlesForCity } from "../data/articles";

const BADGES = ["SIA Licensed", "BS 7858 Vetted", "ISO 9001", "£10m Insured"];

export default function CityPage() {
  const { slug } = useParams();
  const loc = getLocation(slug);
  if (!loc) return <NotFound />;

  const content = cityContent(loc);
  const guide = cityGuide(loc.slug);
  const intro = guide ? guide.intro : content.intro;
  const local = guide ? guide.local : content.local;
  const approach = guide ? guide.approach : content.approach;
  const areas = guide ? guide.areas : content.areas;
  const faqs = guide ? guide.faqs : content.faqs;
  const skyline = citySkyline(loc.slug);
  const heroImg = skyline || IMAGES.guards[hash(loc.slug) % IMAGES.guards.length];
  const heroAlt = skyline
    ? skyline.alt
    : `Security officer on duty in ${loc.name}, ${loc.county}`;
  const articles = articlesForCity(loc.slug, 2);

  return (
    <>
      <Seo
        title={`Security Guards & Services in ${loc.name} | Xtreme Protection England`}
        description={
          guide
            ? guide.meta
            : `SIA-licensed security guards in ${loc.name}, ${loc.county}: manned guarding, mobile patrols, CCTV monitoring, key holding & 24/7 response across ${loc.region}. Call ${SITE.phone}.`
        }
        keywords={`security guards ${loc.name}, security services ${loc.name}, security company ${loc.name}, SIA security ${loc.name}, manned guarding ${loc.name}, mobile patrols ${loc.name}, CCTV monitoring ${loc.name}, security ${loc.county}, 24 hour security ${loc.name}`}
        canonical={`${SITE.domain}/locations/${loc.slug}`}
        noindex={!KEEP.has(`/locations/${loc.slug}`)}
      />

      <section className="relative tactical-grid border-b border-white/10 overflow-hidden" data-testid="city-hero">
        <div className="absolute inset-0">
          <img src={heroImg.url} alt={heroAlt} className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-ink/80 via-ink/70 to-ink"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
          <nav className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-8" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-amber transition-colors">Home</Link>
            <span className="mx-2 text-amber">//</span>
            <Link to="/locations" className="hover:text-amber transition-colors">Locations</Link>
            <span className="mx-2 text-amber">//</span>
            <span className="text-white">{loc.name}</span>
          </nav>
          <Reveal>
            <Eyebrow>{loc.county} // {loc.region}</Eyebrow>
            <h1 className="font-display font-black uppercase leading-[0.95] tracking-tight text-5xl sm:text-6xl lg:text-7xl text-white max-w-4xl">
              Security guard services in {loc.name}
            </h1>
            {intro.map((p, i) => (
              <p key={i} className="mt-6 max-w-3xl text-base sm:text-lg text-gray-300 leading-relaxed">
                {p}
              </p>
            ))}
            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href={SITE.phoneHref}
                data-testid="city-call-btn"
                className="group flex items-center gap-3 bg-amber text-ink font-display font-bold uppercase tracking-widest text-sm px-7 py-4 hover:bg-white transition-colors duration-300"
              >
                <Phone className="w-4 h-4" /> Call {SITE.phone}
              </a>
              <a
                href={SITE.emailHref}
                data-testid="city-email-btn"
                className="group flex items-center gap-3 border border-white/25 text-white font-display font-bold uppercase tracking-widest text-sm px-7 py-4 hover:border-amber hover:text-amber transition-colors duration-300"
              >
                <Mail className="w-4 h-4" /> {SITE.email}
              </a>
            </div>
            <div className="mt-8 flex flex-wrap gap-2" data-testid="city-keyword-chips">
              {[`Security Company ${loc.name}`, `Security Guards ${loc.name}`, `SIA Security ${loc.name}`, `24 Hour Security ${loc.name}`].map((k) => (
                <span key={k} className="font-mono text-[10px] uppercase tracking-widest text-amber border border-amber/30 px-2.5 py-1.5">
                  {k}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-20" data-testid="city-services">
        <ChapterHeading number="01" label="SERVICES IN THIS CITY" />
        <Reveal>
          <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl text-white leading-tight mb-4">
            Security services in {loc.name}
          </h2>
          <p className="max-w-2xl text-sm sm:text-base text-gray-400 leading-relaxed mb-10">
            All 17 specialist services are deployed locally in {loc.name}. Open
            any service for {loc.name}-specific coverage, deployment and pricing
            guidance.
          </p>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="city-services-grid">
          {SERVICES.map((svc, i) => (
            <Reveal key={svc.slug} delay={(i % 3) * 0.05}>
              <Link
                to={`/locations/${loc.slug}/${svc.slug}`}
                data-testid={`city-service-${svc.slug}`}
                className="group flex items-center justify-between gap-3 border border-white/10 bg-card px-5 py-4 hover:border-amber transition-colors duration-300 h-full"
              >
                <span>
                  <span className="block font-display font-bold uppercase tracking-wide text-base text-white group-hover:text-amber transition-colors leading-tight">
                    {svc.short} {loc.name}
                  </span>
                  <span className="block font-mono text-[9px] uppercase tracking-widest text-gray-500 mt-1">
                    {svc.keywords[0]}
                  </span>
                </span>
                <ArrowRight className="w-4 h-4 text-amber shrink-0 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-white/10 bg-coal/30 py-16 sm:py-20" data-testid="city-local">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <ChapterHeading number="02" label="LOCAL KNOWLEDGE" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <Reveal className="space-y-5">
              {local.map((p, i) => (
                <p key={i} className="text-sm sm:text-base text-gray-400 leading-relaxed">
                  {p}
                </p>
              ))}
              {areas && (
                <div data-testid="city-areas">
                  <h3 className="font-display font-bold uppercase tracking-wide text-lg text-white pt-2 mb-3">
                    Areas we cover across {loc.name}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {areas.map((a) => (
                      <span key={a} className="font-mono text-[10px] uppercase tracking-widest text-gray-400 border border-white/10 px-2.5 py-1.5">
                        {a}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </Reveal>
            <Reveal delay={0.1}>
              <h3 className="font-display font-bold uppercase tracking-wide text-xl text-white mb-5">
                Nearby areas we cover
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="city-nearby">
                {content.nearby.map((n) => (
                  <Link
                    key={n.slug}
                    to={`/locations/${n.slug}`}
                    data-testid={`nearby-${n.slug}`}
                    className="group flex items-center justify-between gap-3 border border-white/10 bg-card px-4 py-3.5 hover:border-amber transition-colors duration-300"
                  >
                    <span>
                      <span className="block font-display font-bold uppercase tracking-wide text-sm text-white group-hover:text-amber transition-colors">
                        Security {n.name}
                      </span>
                      <span className="block font-mono text-[9px] uppercase tracking-widest text-gray-500 mt-0.5">
                        {n.county}
                      </span>
                    </span>
                    <MapPin className="w-3.5 h-3.5 text-amber shrink-0" />
                  </Link>
                ))}
              </div>
              <Link
                to="/locations"
                data-testid="city-all-locations-link"
                className="group mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-amber hover:text-white transition-colors"
              >
                View all 120+ England locations
                <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <CityMap loc={loc} />

      <section className="border-t border-white/10 bg-coal/30 py-16 sm:py-20" data-testid="city-approach">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <ChapterHeading number="04" label={`OUR APPROACH IN ${loc.name.toUpperCase()}`} />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <Reveal>
              <p className="text-sm sm:text-base text-gray-400 leading-relaxed">
                {approach}
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="flex flex-wrap gap-2">
                {BADGES.map((b) => (
                  <span key={b} className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-amber border border-amber/30 px-2.5 py-1.5">
                    <ShieldCheck className="w-3 h-3" /> {b}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 sm:py-20" data-testid="city-intel">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <ChapterHeading number="05" label="FROM THE INTEL DESK" />
          <Reveal>
            <h2 className="font-display font-extrabold uppercase tracking-tight text-2xl sm:text-3xl text-white leading-tight mb-8">
              Reading for {loc.name} buyers
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-4xl">
            {articles.map((a) => (
              <ArticleCard key={a.slug} article={a} prefix="city-article" />
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 bg-coal/30 py-16 sm:py-20" data-testid="city-faq">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <ChapterHeading number="06" label="QUESTIONS" />
          <Reveal>
            <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl text-white leading-tight mb-10">
              Security in {loc.name} — answered
            </h2>
            <FaqAccordion faqs={faqs} testidPrefix="city-faq" />
          </Reveal>
        </div>
      </section>

      <CtaBand
        title={`Deploy security in ${loc.name} today`}
        text={`Free risk assessment for any site in ${loc.name} or ${loc.county}. Call ${SITE.phone} or email ${SITE.email} — urgent deployments typically within 24 hours.`}
      />
    </>
  );
}
